import { isPhoneLayout } from '../mobile/layout';
import { Application, Container, Point, FederatedPointerEvent } from 'pixi.js';

/**
 * A pannable + zoomable camera. Holds a world container inside the app
 * viewport: drag (or one-finger) to pan, wheel/trackpad or pinch to zoom toward
 * a focal point, all clamped to world bounds. Distinguishes tap from drag so
 * callers can ignore plot taps that were really pans.
 */
export interface Camera {
  world: Container;
  dragged: boolean;
  /**
   * How far the pointer actually travelled during this press, in pixels
   * (Manhattan). `dragged` is this measured against one threshold chosen for
   * PANNING; a tap target that wants a more forgiving tolerance — a finger on a
   * building sprite routinely wobbles past the pan threshold — reads the raw
   * distance and decides for itself instead of being told "that was a pan".
   */
  dragMagnitude: number;
  /**
   * Suspend panning for the rest of this gesture.
   *
   * Set while a drag is PAINTING a run of tiles (see views/farm/paint-run.ts).
   * Painting and panning are the same physical gesture, and if both ran the world
   * would slide out from under the finger — you would paint a curve you never drew,
   * because the tiles under the pointer keep changing while the pointer stands
   * still. The paint owns the gesture; the camera gets it back on the next press.
   */
  panLocked: boolean;
  centerOn(wx: number, wy: number): void;
  /**
   * Ease the camera toward (wx,wy), called every frame while the hero moves.
   *
   * Two things stop this being a hard centre. A deadzone: the target may drift
   * within DEADZONE_TILES of the middle without the camera reacting at all, so
   * small steps and idle fidgeting don't shove the whole world around. And a
   * lerp on whatever is left, so when the camera does move it eases in rather
   * than welding the hero to the centre of the screen.
   */
  follow(wx: number, wy: number, dt: number): void;
  clamp(): void;
  /** Multiply zoom by `factor`, keeping screen point (cx,cy) fixed (default: viewport centre). */
  zoomBy(factor: number, cx?: number, cy?: number): void;
  getScale(): number;
  destroy(): void;
}

const DRAG_THRESHOLD = 6; // px before a press becomes a pan
const MAX_SCALE = 2.5;
// You can zoom OUT at most this many times from full zoom-in. Zooming all the way
// out on a big map renders thousands of tiles at once → lag; capping it keeps the
// visible tile count bounded (and keeps trees/water/decor on screen instead of a
// tiny blur). Floor scale = MAX_SCALE / MAX_ZOOM_OUT_RATIO.
const MAX_ZOOM_OUT_RATIO = 5;
const FOLLOW_LERP = 0.12;   // fraction of the remaining gap closed per 60fps frame
const DEADZONE_TILES = 2;   // free movement around the centre before the camera reacts
const TILE_PX = 48;

export function attachCamera(app: Application, worldW: number, worldH: number): Camera {
  const world = new Container();
  app.stage.addChild(world);
  app.stage.eventMode = 'static';
  app.stage.hitArea = app.screen;

  let scale = 1;
  const pointers = new Map<number, Point>();
  let panning = false, moved = 0, pinchDist = 0;
  const last = new Point();

  const clampN = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
  // Max zoom-out = the map COVERS the viewport (larger ratio), so you can never
  // shrink the map smaller than its own frame / see empty space beyond the edges —
  // AND never past MAX_SCALE/MAX_ZOOM_OUT_RATIO, so a big map can't zoom out to a
  // laggy, thousands-of-tiles blur.
  const minScale = () => Math.max(app.screen.width / worldW, app.screen.height / worldH, MAX_SCALE / MAX_ZOOM_OUT_RATIO);

  function clamp() {
    const vw = app.screen.width, vh = app.screen.height;
    const sw = worldW * scale, sh = worldH * scale;
    world.x = sw <= vw ? (vw - sw) / 2 : clampN(world.x, vw - sw, 0);
    world.y = sh <= vh ? (vh - sh) / 2 : clampN(world.y, vh - sh, 0);
  }
  function centerOn(wx: number, wy: number) {
    world.x = app.screen.width / 2 - wx * scale;
    world.y = app.screen.height / 2 - wy * scale;
    clamp();
  }

  function follow(wx: number, wy: number, dt: number) {
    const vw = app.screen.width, vh = app.screen.height;
    // Where the target currently sits on screen, vs where the camera is centred.
    const sx = wx * scale + world.x;
    const sy = wy * scale + world.y;
    const offX = sx - vw / 2;
    const offY = sy - vh / 2;
    const dead = DEADZONE_TILES * TILE_PX * scale;

    // Only chase the part of the offset that has left the deadzone box.
    const overX = Math.abs(offX) > dead ? offX - Math.sign(offX) * dead : 0;
    const overY = Math.abs(offY) > dead ? offY - Math.sign(offY) * dead : 0;
    if (!overX && !overY) return;

    // Frame-rate independent lerp. A raw `k` per frame would ease twice as fast
    // at 120fps as at 60; this converts it to a per-second rate so the feel is
    // the same on any display.
    const k = 1 - Math.pow(1 - FOLLOW_LERP, dt * 60);
    world.x -= overX * k;
    world.y -= overY * k;
    clamp();
  }
  function zoomBy(factor: number, cx = app.screen.width / 2, cy = app.screen.height / 2) {
    const next = clampN(scale * factor, minScale(), MAX_SCALE);
    const applied = next / scale;
    if (applied === 1) return;
    scale = next;
    world.scale.set(scale);
    world.x = cx - (cx - world.x) * applied;
    world.y = cy - (cy - world.y) * applied;
    clamp();
  }

  // ── Pointer pan + pinch ─────────────────────────────────────────────────────
  const dist = () => {
    const [a, b] = [...pointers.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };
  const mid = () => {
    const [a, b] = [...pointers.values()];
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  };
  const onDown = (e: FederatedPointerEvent) => {
    pointers.set(e.pointerId, e.global.clone());
    // A fresh press always hands the camera back: the paint lock is per-gesture,
    // and a lock left standing (a pointerup the stage never saw — finger off the
    // edge, tab switch) would silently kill panning for good.
    if (pointers.size === 1) { panning = true; moved = 0; last.copyFrom(e.global); cam.dragged = false; cam.dragMagnitude = 0; cam.panLocked = false; }
    else if (pointers.size === 2) { panning = false; pinchDist = dist(); }
  };
  const onMove = (e: FederatedPointerEvent) => {
    if (pointers.has(e.pointerId)) pointers.get(e.pointerId)!.copyFrom(e.global);
    if (pointers.size >= 2) {
      const d = dist();
      if (pinchDist > 0) { const m = mid(); zoomBy(d / pinchDist, m.x, m.y); }
      pinchDist = d;
      cam.dragged = true;
      cam.dragMagnitude = Number.POSITIVE_INFINITY;   // a pinch is never a tap
      return;
    }
    if (!panning) return;
    // Painting owns this gesture — keep tracking the pointer so the camera does not
    // jump when the lock is released, but move nothing.
    if (cam.panLocked) { last.copyFrom(e.global); return; }
    const dx = e.global.x - last.x, dy = e.global.y - last.y;
    moved += Math.abs(dx) + Math.abs(dy);
    world.x += dx; world.y += dy;
    last.copyFrom(e.global);
    clamp();
    cam.dragMagnitude = moved;
    if (moved > DRAG_THRESHOLD) cam.dragged = true;
  };
  const onUp = (e: FederatedPointerEvent) => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinchDist = 0;
    if (pointers.size === 1) { panning = true; last.copyFrom([...pointers.values()][0]); }
    else if (pointers.size === 0) panning = false;
  };
  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const rect = app.canvas.getBoundingClientRect();
    // Zoom by the scroll magnitude but gently, and clamp each event so a
    // trackpad's burst of events can't zoom the whole way in one flick.
    const f = clampN(Math.exp(-e.deltaY * 0.0016), 0.9, 1.1);
    zoomBy(f, e.clientX - rect.left, e.clientY - rect.top);
  };

  // Phones: 16px tiles at scale 1 are too small to read or tap accurately, so
  // start zoomed in. Clamped to the legal range in case the map is tiny.
  //
  // `isPhoneLayout()` rather than `innerWidth <= 640`: a landscape phone is 844
  // wide, so the old width-only test missed the exact device the rule was written
  // for and started it at scale 1 on a 390px-tall screen.
  if (isPhoneLayout()) {
    scale = clampN(1.7, minScale(), MAX_SCALE);
    world.scale.set(scale);
  }

  // CAPTURE phase, not bubble. Sprites that handle their own press (trees, rocks,
  // props) call stopPropagation() at target — which is BEFORE the bubble pass ever
  // reaches the stage, so a bubble-phase listener here never ran for those presses
  // and `dragged`/`dragMagnitude` kept the values left by the PREVIOUS gesture.
  // After any pan, every following prop tap was then judged "that was a pan" and
  // dropped, and stayed dropped until the player happened to tap bare ground.
  // Capture runs root→target before the target's handlers, so the counters are
  // always reset for the gesture they are about to describe. Pixi also fires
  // `*capture` listeners AT_TARGET, so a press on the stage itself still counts.
  app.stage.on('pointerdowncapture', onDown);
  app.stage.on('pointermove', onMove);
  app.stage.on('pointerup', onUp);
  app.stage.on('pointerupoutside', onUp);
  app.canvas.addEventListener('wheel', onWheel, { passive: false });

  const cam: Camera = {
    world, dragged: false, dragMagnitude: 0, panLocked: false, centerOn, follow, clamp, zoomBy,
    getScale: () => scale,
    destroy() {
      app.stage.off('pointerdowncapture', onDown);
      app.stage.off('pointermove', onMove);
      app.stage.off('pointerup', onUp);
      app.stage.off('pointerupoutside', onUp);
      app.canvas.removeEventListener('wheel', onWheel);
    },
  };
  return cam;
}
